import React from "react";
import { useParams } from "react-router";
import { usePayout } from "@/_actions/researchActions";
import { Copy, Paperclip, CheckCircle } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import Dinero from "dinero.js";
import FileUploadButton from "@/components/forms/FileUploadButton";
import { payoutStatusClassNames, payoutStatusLabels } from "./payoutStatus";
import { ANALYTICS_EVENTS, track } from "@/utils/analytics";

function PayoutStatusBadge({ status }) {
  return (
    <span
      className={`px-2 py-1 rounded-full text-xs font-medium ${payoutStatusClassNames[status] || "bg-zinc-100 text-zinc-800"}`}
    >
      {payoutStatusLabels[status] || status}
    </span>
  );
}

// What the broker should know about the payout at each status once the invoice is handled
function PayoutStatusMessage({ payout }) {
  const hasInvoices = payout.pdf_invoice && payout.xml_invoice;
  const messages = {
    pending: hasInvoices && {
      title: "Pago en proceso",
      body: "Estamos revisando tu factura. Recibirás una notificación una vez que se complete.",
    },
    approved: {
      title: "Solicitud aprobada",
      body: "Tu factura fue aprobada y el pago está en proceso de transferencia.",
    },
    paid: {
      title: "Pago realizado",
      body: "El pago de esta solicitud ya fue transferido a tu cuenta.",
    },
    rejected: {
      title: "Solicitud rechazada",
      body: "Las investigaciones de esta solicitud vuelven a estar disponibles para que solicites su pago de nuevo con una factura corregida.",
    },
  };
  const message = messages[payout.status];
  if (!message) return null;

  return (
    <section className="bg-white rounded-xl p-6 space-y-3">
      <h3 className="text-zinc-800 text-xl font-bold leading-relaxed tracking-tight">
        {message.title}
      </h3>
      {payout.status === "rejected" && payout.rejection_reason && (
        <div className="rounded-md border border-red-200 bg-red-50 p-4 text-red-900">
          <p className="font-medium">Motivo del rechazo</p>
          <p className="whitespace-pre-line">{payout.rejection_reason}</p>
        </div>
      )}
      <p>{message.body}</p>
    </section>
  );
}

export default function Detail() {
  const { id } = useParams();
  const { data, isLoading, error, mutate } = usePayout(id);

  const handleUploadSuccess = (s3Key, fileName, fileType) => {
    console.log(`${fileType} uploaded successfully:`, { s3Key, fileName });
    track(ANALYTICS_EVENTS.COMMISSION_INVOICE_UPLOADED, {
      payout_id: id,
      file_type: fileType.toLowerCase(),
    });
    // Trigger a refetch of payout data to show updated invoice status
    mutate();
  };

  const handleUploadError = (error, fileType) => {
    console.error(`Error uploading ${fileType}:`, error);
    track(ANALYTICS_EVENTS.COMMISSION_INVOICE_UPLOAD_FAILED, {
      payout_id: id,
      file_type: fileType.toLowerCase(),
    });
  };

  if (isLoading) return <div>Loading...</div>;
  if (error) return <div>Error: {error.message}</div>;

  return (
    <div className="space-y-6">
      <section className="bg-white rounded-xl p-6 space-y-3">
        <div className="flex flex-wrap items-center gap-3">
          <h3 className="text-zinc-800 text-xl font-bold leading-relaxed tracking-tight">
            Solicitud de pago {data.id}
          </h3>
          <PayoutStatusBadge status={data.status} />
        </div>

        <ul className="text-center">
          <li>Subtotal: {new Dinero({ amount: data.base, currency: "MXN" }).toFormat()}</li>
          <li>IVA: {new Dinero({ amount: data.tax, currency: "MXN" }).toFormat()}</li>
          <li>Total: {new Dinero({ amount: data.total, currency: "MXN" }).toFormat()}</li>
        </ul>
      </section>

      {data.status === "pending" && (!data.pdf_invoice || !data.xml_invoice) ? (
        <section className="bg-white rounded-xl p-6 space-y-3">
          <div className="flex">
            <div>
              <h3 className="text-zinc-800 text-xl font-bold leading-relaxed tracking-tight">
                Datos de facturación
              </h3>
              <p>Para poder procesar tu pago, necesitamos que subas la factura correspondiente.</p>
            </div>

            <div className="flex-grow" />
            <div>
              <Tooltip>
                <TooltipTrigger>
                  <Copy
                    onClick={(e) => {
                      e.preventDefault();
                      const content = document.getElementById("billing-info").innerHTML;
                      toast.success("Información de facturación copiada al portapapeles");
                      navigator.clipboard
                        .writeText(content)
                        .then(() =>
                          track(ANALYTICS_EVENTS.COMMISSION_TAX_DATA_COPIED, {
                            payout_id: id,
                            field: "all",
                          }),
                        )
                        .catch((err) => {
                          $log.error("Something went wrong", err);
                        });
                    }}
                    className="text-zinc-600 hover:text-zinc-800 cursor-pointer"
                    size={20}
                  />
                </TooltipTrigger>
                <TooltipContent>Copiar información de facturación</TooltipContent>
              </Tooltip>
            </div>
          </div>
          <h4 className="text-zinc-600 text-sm leading-relaxed tracking-tight">
            Emitir factura con los siguientes datos:*
          </h4>

          <div className="bg-stone-50 p-4 space-y-5">
            <p>
              Concepto: Comisión por localización de clientes
              <br />
              Código de SAT: 80141600
            </p>
            <p id="billing-info">
              MARKETING DIGITAL CASAS Y TERRENOS
              <br />
              LA PERPETUA 1668, JARDINES DEL COUNTRY
              <br />
              GUADALAJARA JALISCO. MÉXICO
              <br />
              CP. 44210
              <br />
              Concepto: Gastos General
            </p>
          </div>

          <div className="flex gap-3">
            {!data.pdf_invoice ? (
              <FileUploadButton
                payoutId={id}
                fileType="pdf"
                size="lg"
                variant="outline"
                acceptedFileTypes=".pdf,application/pdf"
                onUploadSuccess={(s3Key, fileName) => handleUploadSuccess(s3Key, fileName, "PDF")}
                onUploadError={(error) => handleUploadError(error, "PDF")}
              >
                Subir factura PDF
              </FileUploadButton>
            ) : (
              <Button disabled={true}>
                <CheckCircle className="inline-block ml-2" size={16} />
                Factura PDF subida
              </Button>
            )}
            {!data.xml_invoice ? (
              <FileUploadButton
                payoutId={id}
                fileType="xml"
                size="lg"
                variant="outline"
                acceptedFileTypes=".xml,application/xml,text/xml"
                onUploadSuccess={(s3Key, fileName) => handleUploadSuccess(s3Key, fileName, "XML")}
                onUploadError={(error) => handleUploadError(error, "XML")}
              >
                Subir factura XML
              </FileUploadButton>
            ) : (
              <Button disabled={true}>
                <CheckCircle className="inline-block ml-2" size={16} />
                Factura XML subida
              </Button>
            )}
          </div>
        </section>
      ) : null}

      <PayoutStatusMessage payout={data} />
    </div>
  );
}
